import React from 'react';
import SEOHelmet from '../components/SEOHelmet';

const PrivacyPolicyPage = () => {
  const lastUpdated = "October 29, 2025";

  return (
    <>
      <SEOHelmet
        title="Privacy Policy - Ceremonzy"
        description="Learn how Ceremonzy collects, uses, and protects your personal information. Our comprehensive privacy policy explains data handling practices."
        keywords="privacy policy, data protection, personal information, ceremonzy privacy, data collection"
        url="/privacy-policy"
      />
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Privacy Policy</h1>
            <p className="text-gray-600 mb-8">Last updated: {lastUpdated}</p>

            <div className="prose prose-lg max-w-none">
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Introduction</h2>
                <p className="text-gray-700 mb-4">
                  Ceremonzy ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                  explains how we collect, use, disclose, and safeguard your information when you visit our 
                  website and use our services, in compliance with the Information Technology Act, 2000 and 
                  the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal 
                  Data or Information) Rules, 2011.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                  <p className="text-blue-800">
                    <strong>Your Consent:</strong> By using Ceremonzy, you consent to the collection and use of 
                    your information as described in this Privacy Policy.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-medium text-gray-800 mb-3">2.1 Personal Information</h3>
                <p className="text-gray-700 mb-3">We may collect the following personal information:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><strong>Account Information:</strong> Name, email address, phone number</li>
                  <li><strong>Profile Information:</strong> Profile photos, business details (for vendors)</li>
                  <li><strong>Contact Information:</strong> Address, location preferences</li>
                  <li><strong>Payment Information:</strong> Billing details (processed securely by third parties)</li>
                  <li><strong>Communication Data:</strong> Messages, reviews, ratings, and feedback</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-800 mb-3">2.2 Automatically Collected Information</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
                  <li><strong>Usage Data:</strong> Pages visited, time spent, click patterns</li>
                  <li><strong>Cookies and Tracking:</strong> Session data, preferences, analytics</li>
                  <li><strong>Location Data:</strong> Approximate location based on IP address</li>
                  <li><strong>Log Data:</strong> Server logs, error reports, security events</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-800 mb-3">2.3 User-Generated Content</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Reviews and ratings submitted by users</li>
                  <li>Photos and media uploaded to profiles or reviews</li>
                  <li>Comments and messages on the platform</li>
                  <li>Business information and service descriptions (vendors)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. How We Use Your Information</h2>
                
                <h3 className="text-xl font-medium text-gray-800 mb-3">3.1 Primary Purposes</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Provide and maintain our platform services</li>
                  <li>Enable connections between users and vendors</li>
                  <li>Process registrations and manage accounts</li>
                  <li>Display vendor profiles and user reviews</li>
                  <li>Facilitate communication between users and vendors</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-800 mb-3">3.2 Analytics and Improvement</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Analyze platform usage and user behavior</li>
                  <li>Improve our services and user experience</li>
                  <li>Develop new features and functionality</li>
                  <li>Monitor platform performance and security</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-800 mb-3">3.3 Communication</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Send service-related notifications</li>
                  <li>Respond to inquiries and support requests</li>
                  <li>Send marketing communications (with consent)</li>
                  <li>Notify about policy updates and important changes</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-800 mb-3">3.4 Legal and Security</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Comply with legal obligations and court orders</li>
                  <li>Prevent fraud and abuse</li>
                  <li>Enforce our Terms of Use</li>
                  <li>Protect user safety and platform integrity</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Cookies and Tracking Technologies</h2>
                <p className="text-gray-700 mb-4">
                  We use cookies and similar tracking technologies to enhance your experience:
                </p>
                
                <h3 className="text-xl font-medium text-gray-800 mb-3">4.1 Types of Cookies</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><strong>Essential Cookies:</strong> Required for platform functionality</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand usage patterns</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                  <li><strong>Marketing Cookies:</strong> Used for targeted advertising (with consent)</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-800 mb-3">4.2 Third-Party Services</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><strong>Google Analytics:</strong> Website traffic and user behavior analysis</li>
                  <li><strong>Cloudinary:</strong> Image hosting and optimization</li>
                  <li><strong>Social Media Plugins:</strong> Social sharing functionality</li>
                </ul>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                  <p className="text-yellow-800">
                    <strong>Cookie Control:</strong> You can control cookies through your browser settings. 
                    However, disabling cookies may affect platform functionality.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Information Sharing and Disclosure</h2>
                
                <h3 className="text-xl font-medium text-gray-800 mb-3">5.1 Public Information</h3>
                <p className="text-gray-700 mb-3">The following information is publicly visible:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Vendor business profiles and contact information</li>
                  <li>User reviews and ratings (with username)</li>
                  <li>Public comments and forum posts</li>
                  <li>Business photos and portfolio images</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-800 mb-3">5.2 Third-Party Service Providers</h3>
                <p className="text-gray-700 mb-3">We may share information with:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Cloud hosting and infrastructure providers</li>
                  <li>Payment processing services</li>
                  <li>Analytics and marketing platforms</li>
                  <li>Customer support and communication tools</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-800 mb-3">5.3 Legal Requirements</h3>
                <p className="text-gray-700 mb-3">We may disclose information when required by:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Court orders or legal processes</li>
                  <li>Government agencies or law enforcement</li>
                  <li>Regulatory compliance requirements</li>
                  <li>Protection of rights, property, or safety</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Data Security</h2>
                <p className="text-gray-700 mb-4">
                  We implement appropriate technical and organizational measures to protect your information:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><strong>Encryption:</strong> Data transmission and storage encryption</li>
                  <li><strong>Access Controls:</strong> Limited access to authorized personnel only</li>
                  <li><strong>Regular Audits:</strong> Security assessments and vulnerability testing</li>
                  <li><strong>Secure Infrastructure:</strong> Industry-standard hosting and database security</li>
                  <li><strong>Incident Response:</strong> Procedures for handling security breaches</li>
                </ul>
                
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                  <p className="text-red-800">
                    <strong>Important:</strong> While we strive to protect your information, no method of 
                    transmission over the internet is 100% secure. You use our platform at your own risk.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Data Retention</h2>
                <p className="text-gray-700 mb-4">We retain your information for the following periods:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li><strong>Account Data:</strong> As long as your account is active</li>
                  <li><strong>Transaction Records:</strong> 7 years for compliance purposes</li>
                  <li><strong>Reviews and Ratings:</strong> Indefinitely (unless removed upon request)</li>
                  <li><strong>Usage Analytics:</strong> Aggregated data retained indefinitely</li>
                  <li><strong>Security Logs:</strong> Up to 2 years for security purposes</li>
                </ul>
                <p className="text-gray-700">
                  You may request deletion of your personal data, subject to legal and legitimate business requirements.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Your Rights and Choices</h2>
                
                <h3 className="text-xl font-medium text-gray-800 mb-3">8.1 Access and Control</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Access and update your account information</li>
                  <li>Download your data in a portable format</li>
                  <li>Delete your account and associated data</li>
                  <li>Opt out of marketing communications</li>
                  <li>Control cookie preferences</li>
                </ul>

                <h3 className="text-xl font-medium text-gray-800 mb-3">8.2 Communication Preferences</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                  <li>Unsubscribe from email newsletters</li>
                  <li>Disable push notifications</li>
                  <li>Control review and rating notifications</li>
                  <li>Opt out of promotional communications</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Children's Privacy</h2>
                <p className="text-gray-700 mb-4">
                  Ceremonzy is not intended for use by children under 13 years of age. We do not knowingly 
                  collect personal information from children under 13. If we become aware that we have 
                  collected personal information from a child under 13, we will take steps to delete such information.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. International Data Transfers</h2>
                <p className="text-gray-700 mb-4">
                  Your information may be transferred to and processed in countries other than India. 
                  We ensure appropriate safeguards are in place to protect your information in accordance 
                  with this Privacy Policy and applicable laws.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Updates to Privacy Policy</h2>
                <p className="text-gray-700 mb-4">
                  We may update this Privacy Policy from time to time. We will notify you of significant 
                  changes by posting the new Privacy Policy on this page and updating the "Last updated" date. 
                  Your continued use of our platform after changes constitutes acceptance of the updated policy.
                </p>
              </section>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;